<!-- Header -->
<div class="flex flex-col border-b border-gray-1 min-h-[76.5px]">
  <div class="flex flex-grow px-3 gap-2">
    <div class="flex flex-col flex-grow pt-3 text-xl font-medium overflow-hidden">
      <div class="overflow-hidden whitespace-nowrap text-ellipsis">Alignments</div>

      <div class="w-[154px] max-w-full">
        <!-- Filter -->
        <mat-select
          class="modus-dropdown slim"
          panelClass="modus-dropdown-panel"
          [formControl]="filterOptionControl"
          disableRipple
        >
          <mat-option [value]="AlignmentListFilterOption.ShowAll">All</mat-option>
          <mat-option [value]="AlignmentListFilterOption.ShowVisible">Visible</mat-option>
        </mat-select>
      </div>
    </div>

    <div class="flex items-center gap-2">
      <input
        hidden
        #fileInput
        type="file"
        accept=".track"
        multiple
        (change)="uploadFiles(fileInput)"
      />

      <button
        modus-icon-button
        modus-tooltip="Create alignment from file"
        class="text-gray-8"
        (click)="fileInput.click()"
      >
        <md-icon>add</md-icon>
      </button>

      <button
        modus-icon-button
        modus-tooltip="Refresh"
        tooltipClass="arrow-right"
        class="text-gray-8"
        [disabled]="disableRefresh()"
        (click)="refresh()"
      >
        <md-icon>refresh</md-icon>
      </button>
    </div>
  </div>
</div>

<div class="flex flex-col flex-1 h-full overflow-hidden">
  @if (loading()) {
    <nzc-busy-indicator [busy]="loading()" class="px-3"></nzc-busy-indicator>
  } @else if (loadingError()) {
    <div class="flex flex-col grow items-center">
      <img src="assets/images/no-alignments.svg" class="mb-3" alt="No Alignments" />
      <div class="text-white bg-red-light rounded-sm p-2 text-sm break-anywhere">
        <div class="font-bold">Error loading data!</div>
        <div class="mt-2">{{ loadingError() }}</div>
      </div>
    </div>
  } @else if (noAlignments()) {
    <div class="flex flex-col grow items-center p-3">
      <img src="assets/images/no-alignments.svg" class="mb-3" alt="No Alignments" />
      <div class="text-center">
        <div class="text-2xl font-semibold">No Alignments</div>
        <div class="mt-2">
          Looks like you haven't added any alignments yet. Start by uploading new items.
        </div>
      </div>
    </div>
  } @else if (noVisibleAlignments()) {
    <div class="flex flex-col grow items-center p-3">
      <img src="assets/images/no-alignments.svg" class="mb-3" alt="No Alignments" />
      <div class="text-center">
        <div class="text-2xl font-semibold">No Visible Alignments</div>
        <div class="mt-2">No alignments are currently visible.</div>
      </div>
    </div>
  } @else {
    <ng-container [ngTemplateOutlet]="itemList"></ng-container>
  }
</div>

<!-- Lists -->
<ng-template #itemList>
  <div class="flex flex-col h-full">
    <div class="flex flex-col h-full overflow-hidden">
      <div
        class="flex flex-col pb-3 flex-auto overflow-y-auto transition-all duration-300"
        [ngClass]="expandMeasurements() ? 'h-[calc(50%-140px)]' : 'h-full'"
        cdkScrollable
      >
        <!-- Source Files (not ingested) -->
        @if (sourceFiles().length > 0) {
          @for (sourceFile of sourceFiles(); track sourceFile.id) {
            <div class="flex flex-row min-h-[32px] text-sm hover:bg-gray-0 px-2">
              <!-- Icon -->
              <div class="flex justify-center items-center min-w-[32px]">
                @switch (sourceFile.state) {
                  <!-- Uploading - Show determinate progress -->
                  @case (SourceFileState.Uploading) {
                    <mat-progress-spinner
                      mode="determinate"
                      class="spinner-inline"
                      [value]="sourceFile.uploadProgress?.percentage"
                    >
                    </mat-progress-spinner>
                  }
                  <!-- Ingesting - Show indeterminate progress -->
                  @case (SourceFileState.Pending) {
                    <mat-progress-spinner
                      mode="indeterminate"
                      class="spinner-inline"
                    ></mat-progress-spinner>
                  }
                  <!-- Failed - Show error icon with error message tooltip-->
                  @case (SourceFileState.Failed) {
                    <md-icon
                      [modus-tooltip]="
                        sourceFile.errors.length > 0
                          ? sourceFile.errors[0].message
                          : 'Error ingesting file'
                      "
                      class="error"
                      tooltipClass="error arrow-left"
                    >
                      alert
                    </md-icon>
                  }
                  <!-- Ingested - Show success icon  -->
                  @case (SourceFileState.Ingested) {
                    <md-icon class="success">check_circle</md-icon>
                  }
                }
              </div>

              <!-- Text -->
              <div
                modus-tooltip="{{ sourceFile.filename }}"
                class="flex items-center w-full overflow-hidden p-1"
              >
                <div
                  class="w-full overflow-hidden text-ellipsis whitespace-nowrap"
                  [ngClass]="{ 'text-red': sourceFile.state === SourceFileState.Failed }"
                >
                  {{ sourceFile.filename }}
                </div>
              </div>

              <!-- Options -->
              <div class="flex flex-row items-center">
                @if (sourceFile.state === SourceFileState.Failed) {
                  <button
                    modus-icon-button
                    modus-tooltip="Delete"
                    tooltipClass="arrow-right"
                    (click)="deleteSourceFile(sourceFile)"
                    [disabled]="sourceFile.deleting"
                  >
                    <md-icon>delete</md-icon>
                  </button>
                }
              </div>
            </div>
          }

          <!-- Divider -->
          <div class="border-b border-gray-1"></div>
        }

        <!-- Alignments -->
        @for (alignment of filteredAlignments(); track alignment.id) {
          <div class="flex flex-row text-sm hover:bg-gray-0 px-2">
            <!-- Icon -->
            <nzc-busy-indicator
              [busy]="alignment.state === AlignmentLoadState.Loading"
              [showLabel]="false"
            >
              @switch (alignment.state) {
                @case (AlignmentLoadState.NotLoaded) {
                  <button
                    modus-icon-button
                    modus-tooltip="Load alignment"
                    tooltipClass="arrow-left"
                    (click)="toggleAlignment(alignment)"
                    [disabled]="alignment.deleting"
                  >
                    <i class="tc-icon-eye-visibility-off-download opacity-60"></i>
                  </button>
                }
                @case (AlignmentLoadState.Ready) {
                  <button
                    modus-icon-button
                    modus-tooltip="Unload alignment"
                    tooltipClass="arrow-left"
                    (click)="toggleAlignment(alignment)"
                    [disabled]="alignment.deleting"
                  >
                    <i class="tc-icon-eye-visibility"></i>
                  </button>
                }
              }
            </nzc-busy-indicator>

            <!-- Text -->
            <div
              modus-tooltip="{{ alignment.name }}"
              class="flex items-center w-full overflow-hidden p-1"
            >
              <div
                class="w-full overflow-hidden text-ellipsis whitespace-nowrap"
                [ngClass]="{ 'text-red': alignment.state === AlignmentLoadState.Failed }"
              >
                {{ alignment.name }}
                @if (alignment.state === AlignmentLoadState.Failed) {
                  <div class="text-small italic text-gray-6">{{ alignment.state }}</div>
                }
              </div>
            </div>

            <!-- Options -->
            <div class="flex flex-row items-center">
              <!-- Active Toggle -->
              @if (alignment.state === AlignmentLoadState.Ready) {
                <button
                  modus-icon-button
                  (click)="toggleActiveAlignment(alignment)"
                  [disabled]="alignment.deleting"
                  [modus-tooltip]="alignment.active ? 'Deactivate' : 'Activate'"
                >
                  @if (!alignment.active) {
                    <md-icon class="opacity-60">lock_open</md-icon>
                  } @else {
                    <md-icon>lock</md-icon>
                  }
                </button>
              }

              <!-- Menu -->
              <button modus-icon-button [modusMenuTriggerFor]="alignmentMenu">
                <md-icon>more_vertical</md-icon>
              </button>
              <md-menu #alignmentMenu>
                <md-menu-item
                  (itemClick)="editTrimBimSettings(alignment)"
                  [disabled]="alignment.deleting"
                  >Display Settings</md-menu-item
                >
                <md-menu-item
                  (itemClick)="deleteAlignment(alignment)"
                  [disabled]="alignment.deleting"
                  >Delete</md-menu-item
                >
              </md-menu>
            </div>
          </div>
        }
      </div>

      <!-- Select Alignment Properties -->
      @if (selectedAlignment()) {
        <div
          class="flex flex-col flex-auto overflow-hidden border-t border-gray-1 transition-all duration-300"
          [ngClass]="expandMeasurements() ? 'h-[calc(50%-140px)]' : 'h-[58px]'"
        >
          <nzc-measurement-list
            [alignment]="selectedAlignment()"
            [expanded]="expandMeasurements()"
            (toggleExpand)="toggleExpandMeasurements()"
          ></nzc-measurement-list>
        </div>
      }
    </div>

    @if (selectedAlignment()) {
      <nzc-alignment-navigation
        class="min-h-[90px] p-3 border-t border-gray-1"
        [alignment]="selectedAlignment()"
      ></nzc-alignment-navigation>
    }
  </div>
</ng-template>
